/**
 * Generated from the server's OpenAPI document by tento-writer-js; do not edit.
 *
 * Optional and nullable fields. A response type (a route's 200 body reaches
 * it) or a request type carries a nullable key as a required
 * `field: Option<T>`, and its `make(fields)` takes every key: a nullable one
 * is stated, `null` when there is none. An optional key that cannot be
 * `null` is `field?: T`. Only a patch type omits a key to mean something.
 *
 * A patch type (a PATCH route's body, or an update the server applies
 * field by field, such as `EventUpdate`) is the one sanctioned
 * `field?: Option<T>`: an absent key leaves the field unchanged, a value sets
 * it, and `null` clears it — where the schema says the field is nullable.
 *
 * @module
 */
import { DateTime, NaiveDate, SemanticTime as _SemanticTime, Utc } from "@tento-chrono";
import { Rational as _Rational } from "@tento-core/rational";
import { Uuid } from "@tento-core/uuid";
import { AuthId as _AuthId, CalendarId as _CalendarId, CellId as _CellId, EventId as _EventId, RowUuid as _RowUuid, SheetUuid as _SheetUuid, SlotId as _SlotId, SyncKey as _SyncKey, TaskId as _TaskId, TemplateRowId as _TemplateRowId, UserId as _UserId } from "@tento-lona/sheets/ids/ids.ts";
import { RowId } from "@tento-lona/sheets/ids/row-id.ts";
import { RowNetworkKey } from "@tento-lona/sheets/ids/row-network-key.ts";
import { SheetId } from "@tento-lona/sheets/ids/sheet-id.ts";
import { SheetNetworkKey } from "@tento-lona/sheets/ids/sheet-network-key.ts";
import { WireDecode, type Option } from "@tento-lona/sheets/wire/wire-decode.ts";
import type { JsonValue as Json } from "@tento-core/json-reader";
import { Field, Model, Scalar } from "@tento-core/model";

/**
 * The server's wire types, generated from its OpenAPI document: one type per
 * schema, each with a validating `hydrate` / `safeHydrate` pair and a
 * `make(fields)` builder that takes every key.
 */
export namespace Network {
  export type AccessRole = "owner" | "viewer" | "editor";
  /** `@tento-lona/sheets/ids/ids.ts`'s `CalendarId`: the one declaration, with its `parse` / `of`. */
  export type CalendarId = _CalendarId;
  export const CalendarId: typeof _CalendarId = _CalendarId;
  /** `@tento-lona/sheets/ids/ids.ts`'s `UserId`: the one declaration, with its `parse` / `of`. */
  export type UserId = _UserId;
  export const UserId: typeof _UserId = _UserId;
  export interface AddCalendarRequest {
    calendarId: CalendarId;
    summary: string;
    userId: UserId;
  }
  export interface AllRowItem {
    attributes: Option<Json>;
    deletedAt: Option<DateTime<Utc>>;
    icon: Option<string>;
    id: RowId;
    label: Option<string>;
    lookupKey: Option<string>;
    rowType: string;
    sheets: string[];
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `AuthId`: the one declaration, with its `parse` / `of`. */
  export type AuthId = _AuthId;
  export const AuthId: typeof _AuthId = _AuthId;
  /** `@tento-lona/sheets/ids/ids.ts`'s `CellId`: the one declaration, with its `parse` / `of`. */
  export type CellId = _CellId;
  export const CellId: typeof _CellId = _CellId;
  export interface SheetCellSchema {
    data: Option<Json>;
    date: SemanticTime;
    endDate: Option<SemanticTime>;
    id: Option<CellId>;
  }
  export type SheetCell = SheetCellSchema;
  export interface DateRange {
    end: NaiveDate;
    start: NaiveDate;
  }
  export type BatchCellStatus = "ok" | "forbidden" | "not_found" | "error";
  /** `@tento-lona/sheets/ids/ids.ts`'s `SyncKey`: the one declaration, with its `parse` / `of`. */
  export type SyncKey = _SyncKey;
  export const SyncKey: typeof _SyncKey = _SyncKey;
  export interface BatchCellResult {
    cells: Option<SheetCell[]>;
    dataRange: Option<DateRange>;
    error: Option<string>;
    metadata: Option<Json>;
    rowKey: RowNetworkKey;
    status: BatchCellStatus;
    syncKeys: Option<SyncKey[]>;
  }
  export interface BatchCellsRequest {
    range: DateRange;
    rowKeys: RowNetworkKey[];
  }
  export interface BatchCellsResponse {
    rows: BatchCellResult[];
  }
  export interface Body {
    date: string;
  }
  export type CalendarAccessRole = "freeBusyReader" | "reader" | "writer" | "owner";
  export interface CalendarCapabilitiesDisplay {
    conference: Option<string>;
    nativeMove: boolean;
    push: boolean;
    writable: boolean;
  }
  export interface CalendarDisplay {
    accessRole: CalendarAccessRole;
    capabilities: Option<CalendarCapabilitiesDisplay>;
    colorId: Option<string>;
    eventsLookupKey: string;
    id: CalendarId;
    source: string;
    summary: Option<string>;
  }
  export interface CalendarList {
    authId: AuthId;
    calendars: CalendarDisplay[];
    isPrimary: boolean;
  }
  export interface CellWriteRequest {
    data: Option<Json>;
    date: Option<SemanticTime>;
    endDate: Option<SemanticTime>;
    id: Option<CellId>;
  }
  export interface CellWriteResponse {
    affectedEnd: SemanticTime;
    affectedStart: SemanticTime;
    cells: SheetCell[];
  }
  export type ConferenceOperation = {
    action: "create";
    kind: Option<string>;
  } | {
    action: "delete";
  };
  export type ConferenceProvider = "google_meet";
  export interface CreateAcl {
    grantee_id: UserId;
    role: AccessRole;
  }
  export type TimeScaleCustom = "year" | "month" | "week" | "day" | "custom";
  export interface SheetRowDb {
    attributes: Option<Json>;
    formula: Option<string>;
    heightPx: Option<number>;
    icon: Option<string>;
    id: RowId;
    label: Option<string>;
    lookupKey: Option<string>;
    metadata: Option<Json>;
    ownerId: UserId;
    timeScale: TimeScaleCustom;
    type: string;
  }
  export interface SheetRowEdge {
    children: Option<RowId[]>;
    position: _Rational;
  }
  export interface SheetRow extends SheetRowDb, SheetRowEdge {
  }
  export interface CreateRowResponse extends SheetRow {
    shouldFetchData: Option<boolean>;
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `RowUuid`: the one declaration, with its `parse` / `of`. */
  export type RowUuid = _RowUuid;
  export const RowUuid: typeof _RowUuid = _RowUuid;
  export interface CreatedRow {
    id: RowUuid;
  }
  export interface DateTimeRange {
    end: DateTime<Utc>;
    start: DateTime<Utc>;
  }
  export interface DeleteAcl {
    user_id: UserId;
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `EventId`: the one declaration, with its `parse` / `of`. */
  export type EventId = _EventId;
  export const EventId: typeof _EventId = _EventId;
  export type EventVisibility = "default" | "public" | "private" | "confidential";
  export interface EventCell {
    attendees: Option<Json>;
    authId: AuthId;
    calendarId: CalendarId;
    category: Option<string>;
    conferenceData: Option<Json>;
    creatorDisplayName: Option<string>;
    creatorEmail: Option<string>;
    creatorId: Option<string>;
    creatorIsSelf: Option<boolean>;
    description: Option<string>;
    endDate: Option<NaiveDate>;
    endTimeZone: Option<string>;
    et: Option<DateTime<Utc>>;
    etag: string;
    eventType: Option<string>;
    icalId: Option<string>;
    id: EventId;
    location: Option<string>;
    queryRangeEnd: Option<DateTime<Utc>>;
    queryRangeStart: Option<DateTime<Utc>>;
    recurrence: Option<string[]>;
    recurringEventId: Option<EventId>;
    source: string;
    st: Option<DateTime<Utc>>;
    startDate: Option<NaiveDate>;
    startTimeZone: Option<string>;
    status: Option<string>;
    suggestedCategory: Option<string>;
    suggestedTimezone: Option<string>;
    t: Option<string>;
    timezone: Option<string>;
    transparency: Option<string>;
    useSuggestedCategory: Option<boolean>;
    visibility: Option<EventVisibility>;
  }
  export interface EventUpdate {
    category?: Option<string>;
    conferenceOperation?: Option<{
      action: "create";
      kind: Option<string>;
    } | {
      action: "delete";
    }>;
    description?: Option<string>;
    endDate?: Option<NaiveDate>;
    inviteeEmails?: Option<string[]>;
    location?: Option<string>;
    recurrence?: Option<string[]>;
    responseStatus?: Option<string>;
    scope?: Option<"this" | "following" | "all">;
    startDate?: Option<NaiveDate>;
    suggestedCategory?: Option<string>;
    time?: Option<DateTimeRange>;
    timezone?: Option<string>;
    title?: Option<string>;
    useSuggestedCategory?: Option<boolean>;
  }
  export interface RowDef {
    attributes: Option<Json>;
    formula: Option<string>;
    heightPx: Option<number>;
    label: Option<string>;
  }
  export interface NewRowWithAttributes extends RowDef {
    clientId: Option<string>;
    icon: Option<string>;
    lookupKey: Option<string>;
    position: Option<_Rational>;
    timeScale: Option<TimeScaleCustom>;
    type: string;
  }
  export interface HydrateChildrenBody {
    children: NewRowWithAttributes[];
  }
  export interface HydratedRow {
    clientId: Option<string>;
    row: SheetRow;
  }
  export interface HydrateChildrenResponse {
    hydratedRows: HydratedRow[];
  }
  export interface LinkRowRequest {
    lookupKey: string;
    position: Option<_Rational>;
    sheetId: SheetNetworkKey;
  }
  export interface LinkableRow {
    description: string;
    icon: Option<string>;
    id: RowId;
    lookupKey: Option<string>;
    rowType: string;
  }
  export interface LogsCell {
    at: DateTime<Utc>;
    level: string;
    message: string;
    source: Option<string>;
  }
  export interface LookupKeyExistsResponse {
    available: boolean;
    conflictingRowLabel: Option<string>;
  }
  export interface NewEvent {
    category: Option<string>;
    conferenceData: Option<{
      action: "create";
      kind: Option<string>;
    } | {
      action: "delete";
    }>;
    description: Option<string>;
    end: Option<DateTime<Utc>>;
    endDate: Option<NaiveDate>;
    eventTimezone: Option<string>;
    inviteeEmails: Option<string[]>;
    location: Option<string>;
    recurrence: Option<string[]>;
    start: Option<DateTime<Utc>>;
    startDate: Option<NaiveDate>;
    suggestedCategory: Option<string>;
    timezone: Option<string>;
    title: Option<string>;
    useSuggestedCategory: Option<boolean>;
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `SheetUuid`: the one declaration, with its `parse` / `of`. */
  export type SheetUuid = _SheetUuid;
  export const SheetUuid: typeof _SheetUuid = _SheetUuid;
  export type SurfaceKind = "header" | "footer";
  export interface NewRow {
    attributes: Option<Json>;
    id: RowUuid;
    label: Option<string>;
    lookupKey: Option<string>;
    parentId: Option<RowUuid>;
    sheetId: Option<SheetUuid>;
    surface: Option<SurfaceKind>;
    type: string;
  }
  export interface NewRowBody {
    attributes: Option<Json>;
    formula: Option<string>;
    id: Option<RowUuid>;
    label: Option<string>;
    lookupKey: Option<string>;
    position: Option<_Rational>;
    surface: Option<SurfaceKind>;
    timeScale: Option<TimeScaleCustom>;
    type: string;
  }
  export type TimeScale = "year" | "month" | "week" | "day";
  export interface NewSheet {
    description: Option<string>;
    lookup_key: Option<string>;
    time_scale: Option<TimeScale>;
    title: Option<string>;
    width: Option<number>;
  }
  export interface NewUserSheet extends NewSheet {
    id: Option<Uuid>;
    position: Option<_Rational>;
    rows: Option<NewRowWithAttributes[]>;
  }
  export type RowSortField = "label" | "type" | "lookupKey" | "sheet" | "deletedAt";
  export type SortDirection = "asc" | "desc";
  export interface PaginatedRowsResponse {
    items: AllRowItem[];
    page: number;
    pageSize: number;
    sortBy: Option<RowSortField>;
    sortDirection: Option<SortDirection>;
    total: number;
  }
  export interface PatchRowResponse {
    shouldRefetchData: Option<boolean>;
  }
  export interface PatchRow_PendingValidation {
    attributes?: Option<Json>;
    formula?: Option<string>;
    heightPx?: Option<number>;
    icon?: Option<string>;
    label?: Option<string>;
    lookupKey?: Option<string>;
    timeScale?: Option<TimeScaleCustom>;
  }
  export type PendingValidation = Option<Json>;
  export interface ResolvedRowInfo {
    id: RowId;
    label: Option<string>;
    lookupKey: Option<string>;
    type: string;
  }
  export interface ResolvedRowsResponse {
    rowCount: number;
    rows: ResolvedRowInfo[];
  }
  export type ResolvedSheetState = "user_created";
  export interface ResolvedSheetKey {
    lookupKey: Option<string>;
    ownerId: Option<UserId>;
    state: ResolvedSheetState;
    userSheetId: Option<SheetId>;
  }
  export interface RestoreRequest {
    position: Option<_Rational>;
    sheetKey: Option<SheetNetworkKey>;
  }
  export interface RowReorderBodySchema {
    parentRowKey: Option<RowNetworkKey>;
    position: _Rational;
    recanonicalizationThreshold: Option<number>;
  }
  export interface Sheet {
    createdAt: DateTime<Utc>;
    creatorId: UserId;
    description: Option<string>;
    id: SheetUuid;
    lastUpdatedAt: DateTime<Utc>;
    lookupKey: Option<string>;
    metadata: Option<Json>;
    timeScale: Option<TimeScale>;
    title: Option<string>;
    width: Option<number>;
  }
  export interface UserSheet extends Sheet {
    position: Option<_Rational>;
    role: AccessRole;
  }
  export interface UserSheetWithRows extends UserSheet {
    rows: SheetRow[];
  }
  export interface RowReorderResponse {
    recanonicalizedSheet: Option<UserSheetWithRows>;
  }
  export interface SheetReorderBodySchema {
    position: _Rational;
    recanonicalizationThreshold: Option<number>;
  }
  export interface SheetReorderResponse {
    recanonicalizedSheets: Option<UserSheet[]>;
  }
  export interface SheetRowDataResponse {
    data: SheetCell[];
    dataRange: Option<DateRange>;
    id: RowId;
    label: Option<string>;
    lookupKey: Option<string>;
    metadata: Option<Json>;
    rowType: Option<string>;
    syncKeys: Option<SyncKey[]>;
  }
  export interface SheetUpdateAttributes {
    description?: Option<string>;
    title?: Option<string>;
    width?: Option<number>;
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `SlotId`: the one declaration, with its `parse` / `of`. */
  export type SlotId = _SlotId;
  export const SlotId: typeof _SlotId = _SlotId;
  export interface SlotCell {
    bookedBy: Option<string>;
    end: DateTime<Utc>;
    id: SlotId;
    start: DateTime<Utc>;
  }
  export interface SyncRange {
    end: NaiveDate;
    start: NaiveDate;
  }
  export interface SyncRowRequest {
    reset: Option<boolean>;
  }
  export interface SyncRowResponse {
    effectiveRange: SyncRange;
    syncKeys: SyncKey[];
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `TaskId`: the one declaration, with its `parse` / `of`. */
  export type TaskId = _TaskId;
  export const TaskId: typeof _TaskId = _TaskId;
  export type TaskPriority = "high" | "medium" | "low";
  export type TaskStatus = "completed" | "inProgress" | "planned" | "autoScheduled";
  export interface TaskAttributes {
    completedAt: Option<DateTime<Utc>>;
    createdAt: Option<DateTime<Utc>>;
    description: Option<string>;
    displayId: Option<string>;
    displayNumber: Option<number>;
    dueDate: Option<DateTime<Utc>>;
    eventId: Option<EventId>;
    importance: Option<TaskPriority>;
    position: Option<_Rational>;
    scheduledEnd: Option<DateTime<Utc>>;
    scheduledStart: Option<DateTime<Utc>>;
    status: Option<TaskStatus>;
    title: Option<string>;
  }
  export interface Task extends TaskAttributes {
    id: TaskId;
    userId: UserId;
  }
  export interface TaskCell extends TaskAttributes {
    id: TaskId;
  }
  export interface TaskUpdateAttributes {
    completedAt?: Option<DateTime<Utc>>;
    description?: Option<string>;
    dueDate?: Option<DateTime<Utc>>;
    eventId?: Option<EventId>;
    importance?: Option<TaskPriority>;
    position?: Option<_Rational>;
    scheduledEnd?: Option<DateTime<Utc>>;
    scheduledStart?: Option<DateTime<Utc>>;
    status?: Option<TaskStatus>;
    title?: Option<string>;
  }
  /** `@tento-lona/sheets/ids/ids.ts`'s `TemplateRowId`: the one declaration, with its `parse` / `of`. */
  export type TemplateRowId = _TemplateRowId;
  export const TemplateRowId: typeof _TemplateRowId = _TemplateRowId;
  export interface TemplateRowDef extends RowDef {
    data: Json[];
    id: TemplateRowId;
    isSystemRow: Option<boolean>;
    lookupKey: Option<string>;
    timeScale: TimeScaleCustom;
    type: Option<string>;
  }
  export interface TimezoneCell {
    transitions: Option<Json[]>;
    tz: string;
  }
  export interface UserSheetAccessSelect {
    accessLevel: AccessRole;
    position: Option<_Rational>;
    spreadsheetId: SheetId;
    userId: UserId;
  }
  export interface UserSheetAttributes extends SheetUpdateAttributes {
    position?: Option<_Rational>;
  }
  export interface WeatherCell {
    astro: Option<Json>;
    date: NaiveDate;
    day: Option<Json>;
    hours: Json[];
    source: string;
    storedAt: DateTime<Utc>;
  }
  export type SemanticTime = _SemanticTime;
  export namespace AddCalendarRequest {
    /** The field table that validates, hydrates and serializes a `AddCalendarRequest`. */
    export const MODEL: Model.Object<AddCalendarRequest> = Model.object<AddCalendarRequest>({
      calendarId: Field.required(Scalar.checked<string, CalendarId>("CalendarId", Scalar.string, CalendarId.parse, null)),
      summary: Field.required(Scalar.string),
      userId: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
    });
    /** The wire JSON `raw` as a `AddCalendarRequest`; throws when it does not match. */
    export const hydrate: (raw: Json) => AddCalendarRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `AddCalendarRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<AddCalendarRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: AddCalendarRequest) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace AllRowItem {
    /** The field table that validates, hydrates and serializes a `AllRowItem`. */
    export const MODEL: Model.Object<AllRowItem> = Model.object<AllRowItem>({
      attributes: WireDecode.nullable(Scalar.json),
      deletedAt: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      icon: WireDecode.nullable(Scalar.string),
      id: Field.required(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null)),
      label: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      rowType: Field.required(Scalar.string),
      sheets: Field.required(Model.array(Scalar.string)),
    });
    /** The wire JSON `raw` as a `AllRowItem`; throws when it does not match. */
    export const hydrate: (raw: Json) => AllRowItem = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `AllRowItem`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<AllRowItem> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: AllRowItem) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetCellSchema {
    /** The field table that validates, hydrates and serializes a `SheetCellSchema`. */
    export const MODEL: Model.Object<SheetCellSchema> = Model.object<SheetCellSchema>({
      data: WireDecode.nullable(Scalar.json),
      date: Field.required(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      endDate: WireDecode.nullable(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      id: WireDecode.nullable(Scalar.checked<string, CellId>("CellId", Scalar.string, CellId.parse, null)),
    });
    /** The wire JSON `raw` as a `SheetCellSchema`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetCellSchema = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetCellSchema`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetCellSchema> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetCellSchema) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace DateRange {
    /** The field table that validates, hydrates and serializes a `DateRange`. */
    export const MODEL: Model.Object<DateRange> = Model.object<DateRange>({
      end: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      start: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
    });
    /** The wire JSON `raw` as a `DateRange`; throws when it does not match. */
    export const hydrate: (raw: Json) => DateRange = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `DateRange`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<DateRange> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: DateRange) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace BatchCellResult {
    /** The field table that validates, hydrates and serializes a `BatchCellResult`. */
    export const MODEL: Model.Object<BatchCellResult> = Model.object<BatchCellResult>({
      cells: WireDecode.nullable(Model.array(Model.defer(() => SheetCellSchema.MODEL))),
      dataRange: WireDecode.nullable(Model.defer(() => DateRange.MODEL)),
      error: WireDecode.nullable(Scalar.string),
      metadata: WireDecode.nullable(Scalar.json),
      rowKey: Field.required(Scalar.checked<string, RowNetworkKey>("RowNetworkKey", Scalar.string, RowNetworkKey.parse, null)),
      status: Field.required(Scalar.stringUnion(["ok", "forbidden", "not_found", "error"] as const)),
      syncKeys: WireDecode.nullable(Model.array(Scalar.checked<string, SyncKey>("SyncKey", Scalar.string, SyncKey.parse, null))),
    });
    /** The wire JSON `raw` as a `BatchCellResult`; throws when it does not match. */
    export const hydrate: (raw: Json) => BatchCellResult = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `BatchCellResult`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<BatchCellResult> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: BatchCellResult) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace BatchCellsRequest {
    /** The field table that validates, hydrates and serializes a `BatchCellsRequest`. */
    export const MODEL: Model.Object<BatchCellsRequest> = Model.object<BatchCellsRequest>({
      range: Field.required(Model.defer(() => DateRange.MODEL)),
      rowKeys: Field.required(Model.array(Scalar.checked<string, RowNetworkKey>("RowNetworkKey", Scalar.string, RowNetworkKey.parse, null))),
    });
    /** The wire JSON `raw` as a `BatchCellsRequest`; throws when it does not match. */
    export const hydrate: (raw: Json) => BatchCellsRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `BatchCellsRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<BatchCellsRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: BatchCellsRequest) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace BatchCellsResponse {
    /** The field table that validates, hydrates and serializes a `BatchCellsResponse`. */
    export const MODEL: Model.Object<BatchCellsResponse> = Model.object<BatchCellsResponse>({
      rows: Field.required(Model.array(Model.defer(() => BatchCellResult.MODEL))),
    });
    /** The wire JSON `raw` as a `BatchCellsResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => BatchCellsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `BatchCellsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<BatchCellsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: BatchCellsResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace Body {
    /** The field table that validates, hydrates and serializes a `Body`. */
    export const MODEL: Model.Object<Body> = Model.object<Body>({
      date: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `Body`; throws when it does not match. */
    export const hydrate: (raw: Json) => Body = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `Body`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<Body> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: Body) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CalendarCapabilitiesDisplay {
    /** The field table that validates, hydrates and serializes a `CalendarCapabilitiesDisplay`. */
    export const MODEL: Model.Object<CalendarCapabilitiesDisplay> = Model.object<CalendarCapabilitiesDisplay>({
      conference: WireDecode.nullable(Scalar.string),
      nativeMove: Field.required(Scalar.boolean),
      push: Field.required(Scalar.boolean),
      writable: Field.required(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `CalendarCapabilitiesDisplay`; throws when it does not match. */
    export const hydrate: (raw: Json) => CalendarCapabilitiesDisplay = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CalendarCapabilitiesDisplay`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CalendarCapabilitiesDisplay> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CalendarCapabilitiesDisplay) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CalendarDisplay {
    /** The field table that validates, hydrates and serializes a `CalendarDisplay`. */
    export const MODEL: Model.Object<CalendarDisplay> = Model.object<CalendarDisplay>({
      accessRole: Field.required(Scalar.stringUnion(["freeBusyReader", "reader", "writer", "owner"] as const)),
      capabilities: WireDecode.nullable(Model.defer(() => CalendarCapabilitiesDisplay.MODEL)),
      colorId: WireDecode.nullable(Scalar.string),
      eventsLookupKey: Field.required(Scalar.string),
      id: Field.required(Scalar.checked<string, CalendarId>("CalendarId", Scalar.string, CalendarId.parse, null)),
      source: Field.required(Scalar.string),
      summary: WireDecode.nullable(Scalar.string),
    });
    /** The wire JSON `raw` as a `CalendarDisplay`; throws when it does not match. */
    export const hydrate: (raw: Json) => CalendarDisplay = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CalendarDisplay`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CalendarDisplay> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CalendarDisplay) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CalendarList {
    /** The field table that validates, hydrates and serializes a `CalendarList`. */
    export const MODEL: Model.Object<CalendarList> = Model.object<CalendarList>({
      authId: Field.required(Scalar.checked<string, AuthId>("AuthId", Scalar.string, AuthId.parse, null)),
      calendars: Field.required(Model.array(Model.defer(() => CalendarDisplay.MODEL))),
      isPrimary: Field.required(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `CalendarList`; throws when it does not match. */
    export const hydrate: (raw: Json) => CalendarList = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CalendarList`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CalendarList> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CalendarList) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CellWriteRequest {
    /** The field table that validates, hydrates and serializes a `CellWriteRequest`. */
    export const MODEL: Model.Object<CellWriteRequest> = Model.object<CellWriteRequest>({
      data: WireDecode.nullable(Scalar.json),
      date: WireDecode.nullable(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      endDate: WireDecode.nullable(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      id: WireDecode.nullable(Scalar.checked<string, CellId>("CellId", Scalar.string, CellId.parse, null)),
    });
    /** The wire JSON `raw` as a `CellWriteRequest`; throws when it does not match. */
    export const hydrate: (raw: Json) => CellWriteRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CellWriteRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CellWriteRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CellWriteRequest) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CellWriteResponse {
    /** The field table that validates, hydrates and serializes a `CellWriteResponse`. */
    export const MODEL: Model.Object<CellWriteResponse> = Model.object<CellWriteResponse>({
      affectedEnd: Field.required(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      affectedStart: Field.required(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      cells: Field.required(Model.array(Model.defer(() => SheetCellSchema.MODEL))),
    });
    /** The wire JSON `raw` as a `CellWriteResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => CellWriteResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CellWriteResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CellWriteResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CellWriteResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CreateAcl {
    /** The field table that validates, hydrates and serializes a `CreateAcl`. */
    export const MODEL: Model.Object<CreateAcl> = Model.object<CreateAcl>({
      grantee_id: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
      role: Field.required(Scalar.stringUnion(["owner", "viewer", "editor"] as const)),
    });
    /** The wire JSON `raw` as a `CreateAcl`; throws when it does not match. */
    export const hydrate: (raw: Json) => CreateAcl = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CreateAcl`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CreateAcl> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CreateAcl) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetRowDb {
    /** The field table that validates, hydrates and serializes a `SheetRowDb`. */
    export const MODEL: Model.Object<SheetRowDb> = Model.object<SheetRowDb>({
      attributes: WireDecode.nullable(Scalar.json),
      formula: WireDecode.nullable(Scalar.string),
      heightPx: WireDecode.nullable(Scalar.number),
      icon: WireDecode.nullable(Scalar.string),
      id: Field.required(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null)),
      label: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      metadata: WireDecode.nullable(Scalar.json),
      ownerId: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
      timeScale: Field.required(Scalar.stringUnion(["year", "month", "week", "day", "custom"] as const)),
      type: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `SheetRowDb`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetRowDb = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetRowDb`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetRowDb> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetRowDb) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetRowEdge {
    /** The field table that validates, hydrates and serializes a `SheetRowEdge`. */
    export const MODEL: Model.Object<SheetRowEdge> = Model.object<SheetRowEdge>({
      children: WireDecode.nullable(Model.array(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null))),
      position: Field.required(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
    });
    /** The wire JSON `raw` as a `SheetRowEdge`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetRowEdge = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetRowEdge`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetRowEdge> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetRowEdge) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetRow {
    /** The field table that validates, hydrates and serializes a `SheetRow`. */
    export const MODEL: Model.Object<SheetRow> = Model.object<SheetRow>({
      ...SheetRowDb.MODEL.fields,
      ...SheetRowEdge.MODEL.fields,
    });
    /** The wire JSON `raw` as a `SheetRow`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetRow = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetRow`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetRow> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetRow) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CreateRowResponse {
    /** The field table that validates, hydrates and serializes a `CreateRowResponse`. */
    export const MODEL: Model.Object<CreateRowResponse> = Model.object<CreateRowResponse>({
      ...SheetRow.MODEL.fields,
      shouldFetchData: WireDecode.nullable(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `CreateRowResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => CreateRowResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CreateRowResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CreateRowResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CreateRowResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace CreatedRow {
    /** The field table that validates, hydrates and serializes a `CreatedRow`. */
    export const MODEL: Model.Object<CreatedRow> = Model.object<CreatedRow>({
      id: Field.required(Scalar.checked<string, RowUuid>("RowUuid", Scalar.string, RowUuid.parse, null)),
    });
    /** The wire JSON `raw` as a `CreatedRow`; throws when it does not match. */
    export const hydrate: (raw: Json) => CreatedRow = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CreatedRow`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<CreatedRow> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CreatedRow) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace DateTimeRange {
    /** The field table that validates, hydrates and serializes a `DateTimeRange`. */
    export const MODEL: Model.Object<DateTimeRange> = Model.object<DateTimeRange>({
      end: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      start: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
    });
    /** The wire JSON `raw` as a `DateTimeRange`; throws when it does not match. */
    export const hydrate: (raw: Json) => DateTimeRange = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `DateTimeRange`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<DateTimeRange> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: DateTimeRange) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace DeleteAcl {
    /** The field table that validates, hydrates and serializes a `DeleteAcl`. */
    export const MODEL: Model.Object<DeleteAcl> = Model.object<DeleteAcl>({
      user_id: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
    });
    /** The wire JSON `raw` as a `DeleteAcl`; throws when it does not match. */
    export const hydrate: (raw: Json) => DeleteAcl = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `DeleteAcl`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<DeleteAcl> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: DeleteAcl) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace EventCell {
    /** The field table that validates, hydrates and serializes a `EventCell`. */
    export const MODEL: Model.Object<EventCell> = Model.object<EventCell>({
      attendees: WireDecode.nullable(Scalar.json),
      authId: Field.required(Scalar.checked<string, AuthId>("AuthId", Scalar.string, AuthId.parse, null)),
      calendarId: Field.required(Scalar.checked<string, CalendarId>("CalendarId", Scalar.string, CalendarId.parse, null)),
      category: WireDecode.nullable(Scalar.string),
      conferenceData: WireDecode.nullable(Scalar.json),
      creatorDisplayName: WireDecode.nullable(Scalar.string),
      creatorEmail: WireDecode.nullable(Scalar.string),
      creatorId: WireDecode.nullable(Scalar.string),
      creatorIsSelf: WireDecode.nullable(Scalar.boolean),
      description: WireDecode.nullable(Scalar.string),
      endDate: WireDecode.nullable(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      endTimeZone: WireDecode.nullable(Scalar.string),
      et: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      etag: Field.required(Scalar.string),
      eventType: WireDecode.nullable(Scalar.string),
      icalId: WireDecode.nullable(Scalar.string),
      id: Field.required(Scalar.checked<string, EventId>("EventId", Scalar.string, EventId.parse, null)),
      location: WireDecode.nullable(Scalar.string),
      queryRangeEnd: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      queryRangeStart: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      recurrence: WireDecode.nullable(Model.array(Scalar.string)),
      recurringEventId: WireDecode.nullable(Scalar.checked<string, EventId>("EventId", Scalar.string, EventId.parse, null)),
      source: Field.required(Scalar.string),
      st: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      startDate: WireDecode.nullable(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      startTimeZone: WireDecode.nullable(Scalar.string),
      status: WireDecode.nullable(Scalar.string),
      suggestedCategory: WireDecode.nullable(Scalar.string),
      suggestedTimezone: WireDecode.nullable(Scalar.string),
      t: WireDecode.nullable(Scalar.string),
      timezone: WireDecode.nullable(Scalar.string),
      transparency: WireDecode.nullable(Scalar.string),
      useSuggestedCategory: WireDecode.nullable(Scalar.boolean),
      visibility: WireDecode.nullable(Scalar.stringUnion(["default", "public", "private", "confidential"] as const)),
    });
    /** The wire JSON `raw` as a `EventCell`; throws when it does not match. */
    export const hydrate: (raw: Json) => EventCell = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `EventCell`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<EventCell> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: EventCell) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace EventUpdate {
    /** The field table that validates, hydrates and serializes a `EventUpdate`. */
    export const MODEL: Model.Object<EventUpdate> = Model.object<EventUpdate>({
      category: Field.optional(Scalar.string),
      conferenceOperation: Field.optional(Model.union([Model.object({ action: Field.required(Scalar.stringUnion(["create"] as const)), kind: WireDecode.nullable(Scalar.string) }), Model.object({ action: Field.required(Scalar.stringUnion(["delete"] as const)) })])),
      description: Field.optional(Scalar.string),
      endDate: Field.optional(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      inviteeEmails: Field.optional(Model.array(Scalar.string)),
      location: Field.optional(Scalar.string),
      recurrence: Field.optional(Model.array(Scalar.string)),
      responseStatus: Field.optional(Scalar.string),
      scope: Field.optional(Scalar.stringUnion(["this", "following", "all"] as const)),
      startDate: Field.optional(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      suggestedCategory: Field.optional(Scalar.string),
      time: Field.optional(Model.defer(() => DateTimeRange.MODEL)),
      timezone: Field.optional(Scalar.string),
      title: Field.optional(Scalar.string),
      useSuggestedCategory: Field.optional(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `EventUpdate`; throws when it does not match. */
    export const hydrate: (raw: Json) => EventUpdate = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `EventUpdate`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<EventUpdate> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: EventUpdate) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace RowDef {
    /** The field table that validates, hydrates and serializes a `RowDef`. */
    export const MODEL: Model.Object<RowDef> = Model.object<RowDef>({
      attributes: WireDecode.nullable(Scalar.json),
      formula: WireDecode.nullable(Scalar.string),
      heightPx: WireDecode.nullable(Scalar.number),
      label: WireDecode.nullable(Scalar.string),
    });
    /** The wire JSON `raw` as a `RowDef`; throws when it does not match. */
    export const hydrate: (raw: Json) => RowDef = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `RowDef`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<RowDef> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: RowDef) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace NewRowWithAttributes {
    /** The field table that validates, hydrates and serializes a `NewRowWithAttributes`. */
    export const MODEL: Model.Object<NewRowWithAttributes> = Model.object<NewRowWithAttributes>({
      ...RowDef.MODEL.fields,
      clientId: WireDecode.nullable(Scalar.string),
      icon: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      timeScale: WireDecode.nullable(Scalar.stringUnion(["year", "month", "week", "day", "custom"] as const)),
      type: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `NewRowWithAttributes`; throws when it does not match. */
    export const hydrate: (raw: Json) => NewRowWithAttributes = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `NewRowWithAttributes`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<NewRowWithAttributes> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: NewRowWithAttributes) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace HydrateChildrenBody {
    /** The field table that validates, hydrates and serializes a `HydrateChildrenBody`. */
    export const MODEL: Model.Object<HydrateChildrenBody> = Model.object<HydrateChildrenBody>({
      children: Field.required(Model.array(Model.defer(() => NewRowWithAttributes.MODEL))),
    });
    /** The wire JSON `raw` as a `HydrateChildrenBody`; throws when it does not match. */
    export const hydrate: (raw: Json) => HydrateChildrenBody = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `HydrateChildrenBody`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<HydrateChildrenBody> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: HydrateChildrenBody) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace HydratedRow {
    /** The field table that validates, hydrates and serializes a `HydratedRow`. */
    export const MODEL: Model.Object<HydratedRow> = Model.object<HydratedRow>({
      clientId: WireDecode.nullable(Scalar.string),
      row: Field.required(Model.defer(() => SheetRow.MODEL)),
    });
    /** The wire JSON `raw` as a `HydratedRow`; throws when it does not match. */
    export const hydrate: (raw: Json) => HydratedRow = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `HydratedRow`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<HydratedRow> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: HydratedRow) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace HydrateChildrenResponse {
    /** The field table that validates, hydrates and serializes a `HydrateChildrenResponse`. */
    export const MODEL: Model.Object<HydrateChildrenResponse> = Model.object<HydrateChildrenResponse>({
      hydratedRows: Field.required(Model.array(Model.defer(() => HydratedRow.MODEL))),
    });
    /** The wire JSON `raw` as a `HydrateChildrenResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => HydrateChildrenResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `HydrateChildrenResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<HydrateChildrenResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: HydrateChildrenResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace LinkRowRequest {
    /** The field table that validates, hydrates and serializes a `LinkRowRequest`. */
    export const MODEL: Model.Object<LinkRowRequest> = Model.object<LinkRowRequest>({
      lookupKey: Field.required(Scalar.string),
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      sheetId: Field.required(Scalar.checked<string, SheetNetworkKey>("SheetNetworkKey", Scalar.string, SheetNetworkKey.parse, null)),
    });
    /** The wire JSON `raw` as a `LinkRowRequest`; throws when it does not match. */
    export const hydrate: (raw: Json) => LinkRowRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `LinkRowRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<LinkRowRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: LinkRowRequest) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace LinkableRow {
    /** The field table that validates, hydrates and serializes a `LinkableRow`. */
    export const MODEL: Model.Object<LinkableRow> = Model.object<LinkableRow>({
      description: Field.required(Scalar.string),
      icon: WireDecode.nullable(Scalar.string),
      id: Field.required(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null)),
      lookupKey: WireDecode.nullable(Scalar.string),
      rowType: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `LinkableRow`; throws when it does not match. */
    export const hydrate: (raw: Json) => LinkableRow = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `LinkableRow`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<LinkableRow> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: LinkableRow) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace LogsCell {
    /** The field table that validates, hydrates and serializes a `LogsCell`. */
    export const MODEL: Model.Object<LogsCell> = Model.object<LogsCell>({
      at: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      level: Field.required(Scalar.string),
      message: Field.required(Scalar.string),
      source: WireDecode.nullable(Scalar.string),
    });
    /** The wire JSON `raw` as a `LogsCell`; throws when it does not match. */
    export const hydrate: (raw: Json) => LogsCell = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `LogsCell`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<LogsCell> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: LogsCell) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace LookupKeyExistsResponse {
    /** The field table that validates, hydrates and serializes a `LookupKeyExistsResponse`. */
    export const MODEL: Model.Object<LookupKeyExistsResponse> = Model.object<LookupKeyExistsResponse>({
      available: Field.required(Scalar.boolean),
      conflictingRowLabel: WireDecode.nullable(Scalar.string),
    });
    /** The wire JSON `raw` as a `LookupKeyExistsResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => LookupKeyExistsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `LookupKeyExistsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<LookupKeyExistsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: LookupKeyExistsResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace NewEvent {
    /** The field table that validates, hydrates and serializes a `NewEvent`. */
    export const MODEL: Model.Object<NewEvent> = Model.object<NewEvent>({
      category: WireDecode.nullable(Scalar.string),
      conferenceData: WireDecode.nullable(Model.union([Model.object({ action: Field.required(Scalar.stringUnion(["create"] as const)), kind: WireDecode.nullable(Scalar.string) }), Model.object({ action: Field.required(Scalar.stringUnion(["delete"] as const)) })])),
      description: WireDecode.nullable(Scalar.string),
      end: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      endDate: WireDecode.nullable(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      eventTimezone: WireDecode.nullable(Scalar.string),
      inviteeEmails: WireDecode.nullable(Model.array(Scalar.string)),
      location: WireDecode.nullable(Scalar.string),
      recurrence: WireDecode.nullable(Model.array(Scalar.string)),
      start: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      startDate: WireDecode.nullable(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      suggestedCategory: WireDecode.nullable(Scalar.string),
      timezone: WireDecode.nullable(Scalar.string),
      title: WireDecode.nullable(Scalar.string),
      useSuggestedCategory: WireDecode.nullable(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `NewEvent`; throws when it does not match. */
    export const hydrate: (raw: Json) => NewEvent = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `NewEvent`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<NewEvent> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: NewEvent) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace NewRow {
    /** The field table that validates, hydrates and serializes a `NewRow`. */
    export const MODEL: Model.Object<NewRow> = Model.object<NewRow>({
      attributes: WireDecode.nullable(Scalar.json),
      id: Field.required(Scalar.checked<string, RowUuid>("RowUuid", Scalar.string, RowUuid.parse, null)),
      label: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      parentId: WireDecode.nullable(Scalar.checked<string, RowUuid>("RowUuid", Scalar.string, RowUuid.parse, null)),
      sheetId: WireDecode.nullable(Scalar.checked<string, SheetUuid>("SheetUuid", Scalar.string, SheetUuid.parse, null)),
      surface: WireDecode.nullable(Scalar.stringUnion(["header", "footer"] as const)),
      type: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `NewRow`; throws when it does not match. */
    export const hydrate: (raw: Json) => NewRow = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `NewRow`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<NewRow> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: NewRow) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace NewRowBody {
    /** The field table that validates, hydrates and serializes a `NewRowBody`. */
    export const MODEL: Model.Object<NewRowBody> = Model.object<NewRowBody>({
      attributes: WireDecode.nullable(Scalar.json),
      formula: WireDecode.nullable(Scalar.string),
      id: WireDecode.nullable(Scalar.checked<string, RowUuid>("RowUuid", Scalar.string, RowUuid.parse, null)),
      label: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      surface: WireDecode.nullable(Scalar.stringUnion(["header", "footer"] as const)),
      timeScale: WireDecode.nullable(Scalar.stringUnion(["year", "month", "week", "day", "custom"] as const)),
      type: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `NewRowBody`; throws when it does not match. */
    export const hydrate: (raw: Json) => NewRowBody = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `NewRowBody`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<NewRowBody> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: NewRowBody) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace NewSheet {
    /** The field table that validates, hydrates and serializes a `NewSheet`. */
    export const MODEL: Model.Object<NewSheet> = Model.object<NewSheet>({
      description: WireDecode.nullable(Scalar.string),
      lookup_key: WireDecode.nullable(Scalar.string),
      time_scale: WireDecode.nullable(Scalar.stringUnion(["year", "month", "week", "day"] as const)),
      title: WireDecode.nullable(Scalar.string),
      width: WireDecode.nullable(Scalar.number),
    });
    /** The wire JSON `raw` as a `NewSheet`; throws when it does not match. */
    export const hydrate: (raw: Json) => NewSheet = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `NewSheet`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<NewSheet> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: NewSheet) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace NewUserSheet {
    /** The field table that validates, hydrates and serializes a `NewUserSheet`. */
    export const MODEL: Model.Object<NewUserSheet> = Model.object<NewUserSheet>({
      ...NewSheet.MODEL.fields,
      id: WireDecode.nullable(Scalar.checked<string, Uuid>("Uuid", Scalar.string, Uuid.parse, null)),
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      rows: WireDecode.nullable(Model.array(Model.defer(() => NewRowWithAttributes.MODEL))),
    });
    /** The wire JSON `raw` as a `NewUserSheet`; throws when it does not match. */
    export const hydrate: (raw: Json) => NewUserSheet = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `NewUserSheet`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<NewUserSheet> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: NewUserSheet) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace PaginatedRowsResponse {
    /** The field table that validates, hydrates and serializes a `PaginatedRowsResponse`. */
    export const MODEL: Model.Object<PaginatedRowsResponse> = Model.object<PaginatedRowsResponse>({
      items: Field.required(Model.array(Model.defer(() => AllRowItem.MODEL))),
      page: Field.required(Scalar.number),
      pageSize: Field.required(Scalar.number),
      sortBy: WireDecode.nullable(Scalar.stringUnion(["label", "type", "lookupKey", "sheet", "deletedAt"] as const)),
      sortDirection: WireDecode.nullable(Scalar.stringUnion(["asc", "desc"] as const)),
      total: Field.required(Scalar.number),
    });
    /** The wire JSON `raw` as a `PaginatedRowsResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => PaginatedRowsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PaginatedRowsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<PaginatedRowsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PaginatedRowsResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace PatchRowResponse {
    /** The field table that validates, hydrates and serializes a `PatchRowResponse`. */
    export const MODEL: Model.Object<PatchRowResponse> = Model.object<PatchRowResponse>({
      shouldRefetchData: WireDecode.nullable(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `PatchRowResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => PatchRowResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PatchRowResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<PatchRowResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PatchRowResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace PatchRow_PendingValidation {
    /** The field table that validates, hydrates and serializes a `PatchRow_PendingValidation`. */
    export const MODEL: Model.Object<PatchRow_PendingValidation> = Model.object<PatchRow_PendingValidation>({
      attributes: Field.optional(Scalar.json),
      formula: Field.optional(Scalar.string),
      heightPx: Field.optional(Scalar.number),
      icon: Field.optional(Scalar.string),
      label: Field.optional(Scalar.string),
      lookupKey: Field.optional(Scalar.string),
      timeScale: Field.optional(Scalar.stringUnion(["year", "month", "week", "day", "custom"] as const)),
    });
    /** The wire JSON `raw` as a `PatchRow_PendingValidation`; throws when it does not match. */
    export const hydrate: (raw: Json) => PatchRow_PendingValidation = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PatchRow_PendingValidation`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<PatchRow_PendingValidation> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PatchRow_PendingValidation) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace ResolvedRowInfo {
    /** The field table that validates, hydrates and serializes a `ResolvedRowInfo`. */
    export const MODEL: Model.Object<ResolvedRowInfo> = Model.object<ResolvedRowInfo>({
      id: Field.required(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null)),
      label: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      type: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ResolvedRowInfo`; throws when it does not match. */
    export const hydrate: (raw: Json) => ResolvedRowInfo = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ResolvedRowInfo`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<ResolvedRowInfo> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ResolvedRowInfo) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace ResolvedRowsResponse {
    /** The field table that validates, hydrates and serializes a `ResolvedRowsResponse`. */
    export const MODEL: Model.Object<ResolvedRowsResponse> = Model.object<ResolvedRowsResponse>({
      rowCount: Field.required(Scalar.number),
      rows: Field.required(Model.array(Model.defer(() => ResolvedRowInfo.MODEL))),
    });
    /** The wire JSON `raw` as a `ResolvedRowsResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => ResolvedRowsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ResolvedRowsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<ResolvedRowsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ResolvedRowsResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace ResolvedSheetKey {
    /** The field table that validates, hydrates and serializes a `ResolvedSheetKey`. */
    export const MODEL: Model.Object<ResolvedSheetKey> = Model.object<ResolvedSheetKey>({
      lookupKey: WireDecode.nullable(Scalar.string),
      ownerId: WireDecode.nullable(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
      state: Field.required(Scalar.stringUnion(["user_created"] as const)),
      userSheetId: WireDecode.nullable(Scalar.checked<string, SheetId>("SheetId", Scalar.string, SheetId.of, null)),
    });
    /** The wire JSON `raw` as a `ResolvedSheetKey`; throws when it does not match. */
    export const hydrate: (raw: Json) => ResolvedSheetKey = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ResolvedSheetKey`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<ResolvedSheetKey> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ResolvedSheetKey) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace RestoreRequest {
    /** The field table that validates, hydrates and serializes a `RestoreRequest`. */
    export const MODEL: Model.Object<RestoreRequest> = Model.object<RestoreRequest>({
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      sheetKey: WireDecode.nullable(Scalar.checked<string, SheetNetworkKey>("SheetNetworkKey", Scalar.string, SheetNetworkKey.parse, null)),
    });
    /** The wire JSON `raw` as a `RestoreRequest`; throws when it does not match. */
    export const hydrate: (raw: Json) => RestoreRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `RestoreRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<RestoreRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: RestoreRequest) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace RowReorderBodySchema {
    /** The field table that validates, hydrates and serializes a `RowReorderBodySchema`. */
    export const MODEL: Model.Object<RowReorderBodySchema> = Model.object<RowReorderBodySchema>({
      parentRowKey: WireDecode.nullable(Scalar.checked<string, RowNetworkKey>("RowNetworkKey", Scalar.string, RowNetworkKey.parse, null)),
      position: Field.required(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      recanonicalizationThreshold: WireDecode.nullable(Scalar.number),
    });
    /** The wire JSON `raw` as a `RowReorderBodySchema`; throws when it does not match. */
    export const hydrate: (raw: Json) => RowReorderBodySchema = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `RowReorderBodySchema`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<RowReorderBodySchema> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: RowReorderBodySchema) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace Sheet {
    /** The field table that validates, hydrates and serializes a `Sheet`. */
    export const MODEL: Model.Object<Sheet> = Model.object<Sheet>({
      createdAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      creatorId: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
      description: WireDecode.nullable(Scalar.string),
      id: Field.required(Scalar.checked<string, SheetUuid>("SheetUuid", Scalar.string, SheetUuid.parse, null)),
      lastUpdatedAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      lookupKey: WireDecode.nullable(Scalar.string),
      metadata: WireDecode.nullable(Scalar.json),
      timeScale: WireDecode.nullable(Scalar.stringUnion(["year", "month", "week", "day"] as const)),
      title: WireDecode.nullable(Scalar.string),
      width: WireDecode.nullable(Scalar.number),
    });
    /** The wire JSON `raw` as a `Sheet`; throws when it does not match. */
    export const hydrate: (raw: Json) => Sheet = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `Sheet`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<Sheet> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: Sheet) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace UserSheet {
    /** The field table that validates, hydrates and serializes a `UserSheet`. */
    export const MODEL: Model.Object<UserSheet> = Model.object<UserSheet>({
      ...Sheet.MODEL.fields,
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      role: Field.required(Scalar.stringUnion(["owner", "viewer", "editor"] as const)),
    });
    /** The wire JSON `raw` as a `UserSheet`; throws when it does not match. */
    export const hydrate: (raw: Json) => UserSheet = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `UserSheet`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<UserSheet> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: UserSheet) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace UserSheetWithRows {
    /** The field table that validates, hydrates and serializes a `UserSheetWithRows`. */
    export const MODEL: Model.Object<UserSheetWithRows> = Model.object<UserSheetWithRows>({
      ...UserSheet.MODEL.fields,
      rows: Field.required(Model.array(Model.defer(() => SheetRow.MODEL))),
    });
    /** The wire JSON `raw` as a `UserSheetWithRows`; throws when it does not match. */
    export const hydrate: (raw: Json) => UserSheetWithRows = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `UserSheetWithRows`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<UserSheetWithRows> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: UserSheetWithRows) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace RowReorderResponse {
    /** The field table that validates, hydrates and serializes a `RowReorderResponse`. */
    export const MODEL: Model.Object<RowReorderResponse> = Model.object<RowReorderResponse>({
      recanonicalizedSheet: WireDecode.nullable(Model.defer(() => UserSheetWithRows.MODEL)),
    });
    /** The wire JSON `raw` as a `RowReorderResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => RowReorderResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `RowReorderResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<RowReorderResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: RowReorderResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetReorderBodySchema {
    /** The field table that validates, hydrates and serializes a `SheetReorderBodySchema`. */
    export const MODEL: Model.Object<SheetReorderBodySchema> = Model.object<SheetReorderBodySchema>({
      position: Field.required(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      recanonicalizationThreshold: WireDecode.nullable(Scalar.number),
    });
    /** The wire JSON `raw` as a `SheetReorderBodySchema`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetReorderBodySchema = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetReorderBodySchema`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetReorderBodySchema> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetReorderBodySchema) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetReorderResponse {
    /** The field table that validates, hydrates and serializes a `SheetReorderResponse`. */
    export const MODEL: Model.Object<SheetReorderResponse> = Model.object<SheetReorderResponse>({
      recanonicalizedSheets: WireDecode.nullable(Model.array(Model.defer(() => UserSheet.MODEL))),
    });
    /** The wire JSON `raw` as a `SheetReorderResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetReorderResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetReorderResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetReorderResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetReorderResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetRowDataResponse {
    /** The field table that validates, hydrates and serializes a `SheetRowDataResponse`. */
    export const MODEL: Model.Object<SheetRowDataResponse> = Model.object<SheetRowDataResponse>({
      data: Field.required(Model.array(Model.defer(() => SheetCellSchema.MODEL))),
      dataRange: WireDecode.nullable(Model.defer(() => DateRange.MODEL)),
      id: Field.required(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null)),
      label: WireDecode.nullable(Scalar.string),
      lookupKey: WireDecode.nullable(Scalar.string),
      metadata: WireDecode.nullable(Scalar.json),
      rowType: WireDecode.nullable(Scalar.string),
      syncKeys: WireDecode.nullable(Model.array(Scalar.checked<string, SyncKey>("SyncKey", Scalar.string, SyncKey.parse, null))),
    });
    /** The wire JSON `raw` as a `SheetRowDataResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetRowDataResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetRowDataResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetRowDataResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetRowDataResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetUpdateAttributes {
    /** The field table that validates, hydrates and serializes a `SheetUpdateAttributes`. */
    export const MODEL: Model.Object<SheetUpdateAttributes> = Model.object<SheetUpdateAttributes>({
      description: Field.optional(Scalar.string),
      title: Field.optional(Scalar.string),
      width: Field.optional(Scalar.number),
    });
    /** The wire JSON `raw` as a `SheetUpdateAttributes`; throws when it does not match. */
    export const hydrate: (raw: Json) => SheetUpdateAttributes = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetUpdateAttributes`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SheetUpdateAttributes> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetUpdateAttributes) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SlotCell {
    /** The field table that validates, hydrates and serializes a `SlotCell`. */
    export const MODEL: Model.Object<SlotCell> = Model.object<SlotCell>({
      bookedBy: WireDecode.nullable(Scalar.string),
      end: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      id: Field.required(Scalar.checked<string, SlotId>("SlotId", Scalar.string, SlotId.parse, null)),
      start: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
    });
    /** The wire JSON `raw` as a `SlotCell`; throws when it does not match. */
    export const hydrate: (raw: Json) => SlotCell = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SlotCell`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SlotCell> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SlotCell) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SyncRange {
    /** The field table that validates, hydrates and serializes a `SyncRange`. */
    export const MODEL: Model.Object<SyncRange> = Model.object<SyncRange>({
      end: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      start: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
    });
    /** The wire JSON `raw` as a `SyncRange`; throws when it does not match. */
    export const hydrate: (raw: Json) => SyncRange = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SyncRange`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SyncRange> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SyncRange) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SyncRowRequest {
    /** The field table that validates, hydrates and serializes a `SyncRowRequest`. */
    export const MODEL: Model.Object<SyncRowRequest> = Model.object<SyncRowRequest>({
      reset: WireDecode.nullable(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `SyncRowRequest`; throws when it does not match. */
    export const hydrate: (raw: Json) => SyncRowRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SyncRowRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SyncRowRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SyncRowRequest) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace SyncRowResponse {
    /** The field table that validates, hydrates and serializes a `SyncRowResponse`. */
    export const MODEL: Model.Object<SyncRowResponse> = Model.object<SyncRowResponse>({
      effectiveRange: Field.required(Model.defer(() => SyncRange.MODEL)),
      syncKeys: Field.required(Model.array(Scalar.checked<string, SyncKey>("SyncKey", Scalar.string, SyncKey.parse, null))),
    });
    /** The wire JSON `raw` as a `SyncRowResponse`; throws when it does not match. */
    export const hydrate: (raw: Json) => SyncRowResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SyncRowResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<SyncRowResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SyncRowResponse) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace TaskAttributes {
    /** The field table that validates, hydrates and serializes a `TaskAttributes`. */
    export const MODEL: Model.Object<TaskAttributes> = Model.object<TaskAttributes>({
      completedAt: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      createdAt: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      description: WireDecode.nullable(Scalar.string),
      displayId: WireDecode.nullable(Scalar.string),
      displayNumber: WireDecode.nullable(Scalar.number),
      dueDate: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      eventId: WireDecode.nullable(Scalar.checked<string, EventId>("EventId", Scalar.string, EventId.parse, null)),
      importance: WireDecode.nullable(Scalar.stringUnion(["high", "medium", "low"] as const)),
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      scheduledEnd: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      scheduledStart: WireDecode.nullable(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      status: WireDecode.nullable(Scalar.stringUnion(["completed", "inProgress", "planned", "autoScheduled"] as const)),
      title: WireDecode.nullable(Scalar.string),
    });
    /** The wire JSON `raw` as a `TaskAttributes`; throws when it does not match. */
    export const hydrate: (raw: Json) => TaskAttributes = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `TaskAttributes`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<TaskAttributes> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: TaskAttributes) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace Task {
    /** The field table that validates, hydrates and serializes a `Task`. */
    export const MODEL: Model.Object<Task> = Model.object<Task>({
      ...TaskAttributes.MODEL.fields,
      id: Field.required(Scalar.checked<string, TaskId>("TaskId", Scalar.string, TaskId.parse, null)),
      userId: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
    });
    /** The wire JSON `raw` as a `Task`; throws when it does not match. */
    export const hydrate: (raw: Json) => Task = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `Task`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<Task> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: Task) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace TaskCell {
    /** The field table that validates, hydrates and serializes a `TaskCell`. */
    export const MODEL: Model.Object<TaskCell> = Model.object<TaskCell>({
      ...TaskAttributes.MODEL.fields,
      id: Field.required(Scalar.checked<string, TaskId>("TaskId", Scalar.string, TaskId.parse, null)),
    });
    /** The wire JSON `raw` as a `TaskCell`; throws when it does not match. */
    export const hydrate: (raw: Json) => TaskCell = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `TaskCell`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<TaskCell> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: TaskCell) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace TaskUpdateAttributes {
    /** The field table that validates, hydrates and serializes a `TaskUpdateAttributes`. */
    export const MODEL: Model.Object<TaskUpdateAttributes> = Model.object<TaskUpdateAttributes>({
      completedAt: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      description: Field.optional(Scalar.string),
      dueDate: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      eventId: Field.optional(Scalar.checked<string, EventId>("EventId", Scalar.string, EventId.parse, null)),
      importance: Field.optional(Scalar.stringUnion(["high", "medium", "low"] as const)),
      position: Field.optional(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      scheduledEnd: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      scheduledStart: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      status: Field.optional(Scalar.stringUnion(["completed", "inProgress", "planned", "autoScheduled"] as const)),
      title: Field.optional(Scalar.string),
    });
    /** The wire JSON `raw` as a `TaskUpdateAttributes`; throws when it does not match. */
    export const hydrate: (raw: Json) => TaskUpdateAttributes = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `TaskUpdateAttributes`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<TaskUpdateAttributes> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: TaskUpdateAttributes) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace TemplateRowDef {
    /** The field table that validates, hydrates and serializes a `TemplateRowDef`. */
    export const MODEL: Model.Object<TemplateRowDef> = Model.object<TemplateRowDef>({
      ...RowDef.MODEL.fields,
      data: Field.required(Model.array(Scalar.json)),
      id: Field.required(Scalar.checked<string, TemplateRowId>("TemplateRowId", Scalar.string, TemplateRowId.parse, null)),
      isSystemRow: WireDecode.nullable(Scalar.boolean),
      lookupKey: WireDecode.nullable(Scalar.string),
      timeScale: Field.required(Scalar.stringUnion(["year", "month", "week", "day", "custom"] as const)),
      type: WireDecode.nullable(Scalar.string),
    });
    /** The wire JSON `raw` as a `TemplateRowDef`; throws when it does not match. */
    export const hydrate: (raw: Json) => TemplateRowDef = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `TemplateRowDef`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<TemplateRowDef> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: TemplateRowDef) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace TimezoneCell {
    /** The field table that validates, hydrates and serializes a `TimezoneCell`. */
    export const MODEL: Model.Object<TimezoneCell> = Model.object<TimezoneCell>({
      transitions: WireDecode.nullable(Model.array(Scalar.json)),
      tz: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `TimezoneCell`; throws when it does not match. */
    export const hydrate: (raw: Json) => TimezoneCell = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `TimezoneCell`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<TimezoneCell> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: TimezoneCell) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace UserSheetAccessSelect {
    /** The field table that validates, hydrates and serializes a `UserSheetAccessSelect`. */
    export const MODEL: Model.Object<UserSheetAccessSelect> = Model.object<UserSheetAccessSelect>({
      accessLevel: Field.required(Scalar.stringUnion(["owner", "viewer", "editor"] as const)),
      position: WireDecode.nullable(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
      spreadsheetId: Field.required(Scalar.checked<string, SheetId>("SheetId", Scalar.string, SheetId.of, null)),
      userId: Field.required(Scalar.checked<string, UserId>("UserId", Scalar.string, UserId.parse, null)),
    });
    /** The wire JSON `raw` as a `UserSheetAccessSelect`; throws when it does not match. */
    export const hydrate: (raw: Json) => UserSheetAccessSelect = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `UserSheetAccessSelect`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<UserSheetAccessSelect> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: UserSheetAccessSelect) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace UserSheetAttributes {
    /** The field table that validates, hydrates and serializes a `UserSheetAttributes`. */
    export const MODEL: Model.Object<UserSheetAttributes> = Model.object<UserSheetAttributes>({
      ...SheetUpdateAttributes.MODEL.fields,
      position: Field.optional(Scalar.checked<{ n: number; d: number; }, _Rational>("_Rational", Model.object<{ n: number; d: number; }>({ n: Field.required(Scalar.integer), d: Field.required(Scalar.integer) }), _Rational.makeOpt, null)),
    });
    /** The wire JSON `raw` as a `UserSheetAttributes`; throws when it does not match. */
    export const hydrate: (raw: Json) => UserSheetAttributes = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `UserSheetAttributes`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<UserSheetAttributes> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: UserSheetAttributes) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace WeatherCell {
    /** The field table that validates, hydrates and serializes a `WeatherCell`. */
    export const MODEL: Model.Object<WeatherCell> = Model.object<WeatherCell>({
      astro: WireDecode.nullable(Scalar.json),
      date: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      day: WireDecode.nullable(Scalar.json),
      hours: Field.required(Model.array(Scalar.json)),
      source: Field.required(Scalar.string),
      storedAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
    });
    /** The wire JSON `raw` as a `WeatherCell`; throws when it does not match. */
    export const hydrate: (raw: Json) => WeatherCell = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `WeatherCell`, or the mismatch as an error result. */
    export const safeHydrate: (raw: Json) => Model.Result<WeatherCell> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: WeatherCell) => Json = MODEL.serialize.bind(MODEL);
  }
  export namespace AddCalendarRequest {
    /** The wire JSON `raw` as a new `AddCalendarRequest`; throws a typed error when it does not match. */
    export function parse(raw: Json): AddCalendarRequest {
      return WireDecode.parse("AddCalendarRequest", AddCalendarRequest.MODEL, raw);
    }
    /** A `AddCalendarRequest` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: AddCalendarRequest): AddCalendarRequest {
      return { ...fields };
    }
  }
  export namespace AllRowItem {
    /** The wire JSON `raw` as a new `AllRowItem`; throws a typed error when it does not match. */
    export function parse(raw: Json): AllRowItem {
      return WireDecode.parse("AllRowItem", AllRowItem.MODEL, raw);
    }
    /** A `AllRowItem` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: AllRowItem): AllRowItem {
      return { ...fields };
    }
  }
  export namespace SheetCellSchema {
    /** The wire JSON `raw` as a new `SheetCellSchema`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetCellSchema {
      return WireDecode.parse("SheetCellSchema", SheetCellSchema.MODEL, raw);
    }
    /** A `SheetCellSchema` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetCellSchema): SheetCellSchema {
      return { ...fields };
    }
  }
  export namespace DateRange {
    /** The wire JSON `raw` as a new `DateRange`; throws a typed error when it does not match. */
    export function parse(raw: Json): DateRange {
      return WireDecode.parse("DateRange", DateRange.MODEL, raw);
    }
    /** A `DateRange` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: DateRange): DateRange {
      return { ...fields };
    }
  }
  export namespace BatchCellResult {
    /** The wire JSON `raw` as a new `BatchCellResult`; throws a typed error when it does not match. */
    export function parse(raw: Json): BatchCellResult {
      return WireDecode.parse("BatchCellResult", BatchCellResult.MODEL, raw);
    }
    /** A `BatchCellResult` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: BatchCellResult): BatchCellResult {
      return { ...fields };
    }
  }
  export namespace BatchCellsRequest {
    /** The wire JSON `raw` as a new `BatchCellsRequest`; throws a typed error when it does not match. */
    export function parse(raw: Json): BatchCellsRequest {
      return WireDecode.parse("BatchCellsRequest", BatchCellsRequest.MODEL, raw);
    }
    /** A `BatchCellsRequest` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: BatchCellsRequest): BatchCellsRequest {
      return { ...fields };
    }
  }
  export namespace BatchCellsResponse {
    /** The wire JSON `raw` as a new `BatchCellsResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): BatchCellsResponse {
      return WireDecode.parse("BatchCellsResponse", BatchCellsResponse.MODEL, raw);
    }
    /** A `BatchCellsResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: BatchCellsResponse): BatchCellsResponse {
      return { ...fields };
    }
  }
  export namespace Body {
    /** The wire JSON `raw` as a new `Body`; throws a typed error when it does not match. */
    export function parse(raw: Json): Body {
      return WireDecode.parse("Body", Body.MODEL, raw);
    }
    /** A `Body` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: Body): Body {
      return { ...fields };
    }
  }
  export namespace CalendarCapabilitiesDisplay {
    /** The wire JSON `raw` as a new `CalendarCapabilitiesDisplay`; throws a typed error when it does not match. */
    export function parse(raw: Json): CalendarCapabilitiesDisplay {
      return WireDecode.parse("CalendarCapabilitiesDisplay", CalendarCapabilitiesDisplay.MODEL, raw);
    }
    /** A `CalendarCapabilitiesDisplay` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CalendarCapabilitiesDisplay): CalendarCapabilitiesDisplay {
      return { ...fields };
    }
  }
  export namespace CalendarDisplay {
    /** The wire JSON `raw` as a new `CalendarDisplay`; throws a typed error when it does not match. */
    export function parse(raw: Json): CalendarDisplay {
      return WireDecode.parse("CalendarDisplay", CalendarDisplay.MODEL, raw);
    }
    /** A `CalendarDisplay` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CalendarDisplay): CalendarDisplay {
      return { ...fields };
    }
  }
  export namespace CalendarList {
    /** The wire JSON `raw` as a new `CalendarList`; throws a typed error when it does not match. */
    export function parse(raw: Json): CalendarList {
      return WireDecode.parse("CalendarList", CalendarList.MODEL, raw);
    }
    /** A `CalendarList` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CalendarList): CalendarList {
      return { ...fields };
    }
  }
  export namespace CellWriteRequest {
    /** The wire JSON `raw` as a new `CellWriteRequest`; throws a typed error when it does not match. */
    export function parse(raw: Json): CellWriteRequest {
      return WireDecode.parse("CellWriteRequest", CellWriteRequest.MODEL, raw);
    }
    /** A `CellWriteRequest` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CellWriteRequest): CellWriteRequest {
      return { ...fields };
    }
  }
  export namespace CellWriteResponse {
    /** The wire JSON `raw` as a new `CellWriteResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): CellWriteResponse {
      return WireDecode.parse("CellWriteResponse", CellWriteResponse.MODEL, raw);
    }
    /** A `CellWriteResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CellWriteResponse): CellWriteResponse {
      return { ...fields };
    }
  }
  export namespace CreateAcl {
    /** The wire JSON `raw` as a new `CreateAcl`; throws a typed error when it does not match. */
    export function parse(raw: Json): CreateAcl {
      return WireDecode.parse("CreateAcl", CreateAcl.MODEL, raw);
    }
    /** A `CreateAcl` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CreateAcl): CreateAcl {
      return { ...fields };
    }
  }
  export namespace SheetRowDb {
    /** The wire JSON `raw` as a new `SheetRowDb`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetRowDb {
      return WireDecode.parse("SheetRowDb", SheetRowDb.MODEL, raw);
    }
    /** A `SheetRowDb` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetRowDb): SheetRowDb {
      return { ...fields };
    }
  }
  export namespace SheetRowEdge {
    /** The wire JSON `raw` as a new `SheetRowEdge`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetRowEdge {
      return WireDecode.parse("SheetRowEdge", SheetRowEdge.MODEL, raw);
    }
    /** A `SheetRowEdge` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetRowEdge): SheetRowEdge {
      return { ...fields };
    }
  }
  export namespace SheetRow {
    /** The wire JSON `raw` as a new `SheetRow`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetRow {
      return WireDecode.parse("SheetRow", SheetRow.MODEL, raw);
    }
    /** A `SheetRow` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetRow): SheetRow {
      return { ...fields };
    }
  }
  export namespace CreateRowResponse {
    /** The wire JSON `raw` as a new `CreateRowResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): CreateRowResponse {
      return WireDecode.parse("CreateRowResponse", CreateRowResponse.MODEL, raw);
    }
    /** A `CreateRowResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CreateRowResponse): CreateRowResponse {
      return { ...fields };
    }
  }
  export namespace CreatedRow {
    /** The wire JSON `raw` as a new `CreatedRow`; throws a typed error when it does not match. */
    export function parse(raw: Json): CreatedRow {
      return WireDecode.parse("CreatedRow", CreatedRow.MODEL, raw);
    }
    /** A `CreatedRow` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: CreatedRow): CreatedRow {
      return { ...fields };
    }
  }
  export namespace DateTimeRange {
    /** The wire JSON `raw` as a new `DateTimeRange`; throws a typed error when it does not match. */
    export function parse(raw: Json): DateTimeRange {
      return WireDecode.parse("DateTimeRange", DateTimeRange.MODEL, raw);
    }
    /** A `DateTimeRange` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: DateTimeRange): DateTimeRange {
      return { ...fields };
    }
  }
  export namespace DeleteAcl {
    /** The wire JSON `raw` as a new `DeleteAcl`; throws a typed error when it does not match. */
    export function parse(raw: Json): DeleteAcl {
      return WireDecode.parse("DeleteAcl", DeleteAcl.MODEL, raw);
    }
    /** A `DeleteAcl` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: DeleteAcl): DeleteAcl {
      return { ...fields };
    }
  }
  export namespace EventCell {
    /** The wire JSON `raw` as a new `EventCell`; throws a typed error when it does not match. */
    export function parse(raw: Json): EventCell {
      return WireDecode.parse("EventCell", EventCell.MODEL, raw);
    }
    /** A `EventCell` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: EventCell): EventCell {
      return { ...fields };
    }
  }
  export namespace EventUpdate {
    /** The wire JSON `raw` as a new `EventUpdate`; throws a typed error when it does not match. */
    export function parse(raw: Json): EventUpdate {
      return WireDecode.parse("EventUpdate", EventUpdate.MODEL, raw);
    }
  }
  export namespace RowDef {
    /** The wire JSON `raw` as a new `RowDef`; throws a typed error when it does not match. */
    export function parse(raw: Json): RowDef {
      return WireDecode.parse("RowDef", RowDef.MODEL, raw);
    }
    /** A `RowDef` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: RowDef): RowDef {
      return { ...fields };
    }
  }
  export namespace NewRowWithAttributes {
    /** The wire JSON `raw` as a new `NewRowWithAttributes`; throws a typed error when it does not match. */
    export function parse(raw: Json): NewRowWithAttributes {
      return WireDecode.parse("NewRowWithAttributes", NewRowWithAttributes.MODEL, raw);
    }
    /** A `NewRowWithAttributes` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: NewRowWithAttributes): NewRowWithAttributes {
      return { ...fields };
    }
  }
  export namespace HydrateChildrenBody {
    /** The wire JSON `raw` as a new `HydrateChildrenBody`; throws a typed error when it does not match. */
    export function parse(raw: Json): HydrateChildrenBody {
      return WireDecode.parse("HydrateChildrenBody", HydrateChildrenBody.MODEL, raw);
    }
    /** A `HydrateChildrenBody` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: HydrateChildrenBody): HydrateChildrenBody {
      return { ...fields };
    }
  }
  export namespace HydratedRow {
    /** The wire JSON `raw` as a new `HydratedRow`; throws a typed error when it does not match. */
    export function parse(raw: Json): HydratedRow {
      return WireDecode.parse("HydratedRow", HydratedRow.MODEL, raw);
    }
    /** A `HydratedRow` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: HydratedRow): HydratedRow {
      return { ...fields };
    }
  }
  export namespace HydrateChildrenResponse {
    /** The wire JSON `raw` as a new `HydrateChildrenResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): HydrateChildrenResponse {
      return WireDecode.parse("HydrateChildrenResponse", HydrateChildrenResponse.MODEL, raw);
    }
    /** A `HydrateChildrenResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: HydrateChildrenResponse): HydrateChildrenResponse {
      return { ...fields };
    }
  }
  export namespace LinkRowRequest {
    /** The wire JSON `raw` as a new `LinkRowRequest`; throws a typed error when it does not match. */
    export function parse(raw: Json): LinkRowRequest {
      return WireDecode.parse("LinkRowRequest", LinkRowRequest.MODEL, raw);
    }
    /** A `LinkRowRequest` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: LinkRowRequest): LinkRowRequest {
      return { ...fields };
    }
  }
  export namespace LinkableRow {
    /** The wire JSON `raw` as a new `LinkableRow`; throws a typed error when it does not match. */
    export function parse(raw: Json): LinkableRow {
      return WireDecode.parse("LinkableRow", LinkableRow.MODEL, raw);
    }
    /** A `LinkableRow` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: LinkableRow): LinkableRow {
      return { ...fields };
    }
  }
  export namespace LogsCell {
    /** The wire JSON `raw` as a new `LogsCell`; throws a typed error when it does not match. */
    export function parse(raw: Json): LogsCell {
      return WireDecode.parse("LogsCell", LogsCell.MODEL, raw);
    }
    /** A `LogsCell` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: LogsCell): LogsCell {
      return { ...fields };
    }
  }
  export namespace LookupKeyExistsResponse {
    /** The wire JSON `raw` as a new `LookupKeyExistsResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): LookupKeyExistsResponse {
      return WireDecode.parse("LookupKeyExistsResponse", LookupKeyExistsResponse.MODEL, raw);
    }
    /** A `LookupKeyExistsResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: LookupKeyExistsResponse): LookupKeyExistsResponse {
      return { ...fields };
    }
  }
  export namespace NewEvent {
    /** The wire JSON `raw` as a new `NewEvent`; throws a typed error when it does not match. */
    export function parse(raw: Json): NewEvent {
      return WireDecode.parse("NewEvent", NewEvent.MODEL, raw);
    }
    /** A `NewEvent` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: NewEvent): NewEvent {
      return { ...fields };
    }
  }
  export namespace NewRow {
    /** The wire JSON `raw` as a new `NewRow`; throws a typed error when it does not match. */
    export function parse(raw: Json): NewRow {
      return WireDecode.parse("NewRow", NewRow.MODEL, raw);
    }
    /** A `NewRow` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: NewRow): NewRow {
      return { ...fields };
    }
  }
  export namespace NewRowBody {
    /** The wire JSON `raw` as a new `NewRowBody`; throws a typed error when it does not match. */
    export function parse(raw: Json): NewRowBody {
      return WireDecode.parse("NewRowBody", NewRowBody.MODEL, raw);
    }
    /** A `NewRowBody` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: NewRowBody): NewRowBody {
      return { ...fields };
    }
  }
  export namespace NewSheet {
    /** The wire JSON `raw` as a new `NewSheet`; throws a typed error when it does not match. */
    export function parse(raw: Json): NewSheet {
      return WireDecode.parse("NewSheet", NewSheet.MODEL, raw);
    }
    /** A `NewSheet` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: NewSheet): NewSheet {
      return { ...fields };
    }
  }
  export namespace NewUserSheet {
    /** The wire JSON `raw` as a new `NewUserSheet`; throws a typed error when it does not match. */
    export function parse(raw: Json): NewUserSheet {
      return WireDecode.parse("NewUserSheet", NewUserSheet.MODEL, raw);
    }
    /** A `NewUserSheet` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: NewUserSheet): NewUserSheet {
      return { ...fields };
    }
  }
  export namespace PaginatedRowsResponse {
    /** The wire JSON `raw` as a new `PaginatedRowsResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): PaginatedRowsResponse {
      return WireDecode.parse("PaginatedRowsResponse", PaginatedRowsResponse.MODEL, raw);
    }
    /** A `PaginatedRowsResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: PaginatedRowsResponse): PaginatedRowsResponse {
      return { ...fields };
    }
  }
  export namespace PatchRowResponse {
    /** The wire JSON `raw` as a new `PatchRowResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): PatchRowResponse {
      return WireDecode.parse("PatchRowResponse", PatchRowResponse.MODEL, raw);
    }
    /** A `PatchRowResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: PatchRowResponse): PatchRowResponse {
      return { ...fields };
    }
  }
  export namespace PatchRow_PendingValidation {
    /** The wire JSON `raw` as a new `PatchRow_PendingValidation`; throws a typed error when it does not match. */
    export function parse(raw: Json): PatchRow_PendingValidation {
      return WireDecode.parse("PatchRow_PendingValidation", PatchRow_PendingValidation.MODEL, raw);
    }
  }
  export namespace ResolvedRowInfo {
    /** The wire JSON `raw` as a new `ResolvedRowInfo`; throws a typed error when it does not match. */
    export function parse(raw: Json): ResolvedRowInfo {
      return WireDecode.parse("ResolvedRowInfo", ResolvedRowInfo.MODEL, raw);
    }
    /** A `ResolvedRowInfo` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: ResolvedRowInfo): ResolvedRowInfo {
      return { ...fields };
    }
  }
  export namespace ResolvedRowsResponse {
    /** The wire JSON `raw` as a new `ResolvedRowsResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): ResolvedRowsResponse {
      return WireDecode.parse("ResolvedRowsResponse", ResolvedRowsResponse.MODEL, raw);
    }
    /** A `ResolvedRowsResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: ResolvedRowsResponse): ResolvedRowsResponse {
      return { ...fields };
    }
  }
  export namespace ResolvedSheetKey {
    /** The wire JSON `raw` as a new `ResolvedSheetKey`; throws a typed error when it does not match. */
    export function parse(raw: Json): ResolvedSheetKey {
      return WireDecode.parse("ResolvedSheetKey", ResolvedSheetKey.MODEL, raw);
    }
    /** A `ResolvedSheetKey` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: ResolvedSheetKey): ResolvedSheetKey {
      return { ...fields };
    }
  }
  export namespace RestoreRequest {
    /** The wire JSON `raw` as a new `RestoreRequest`; throws a typed error when it does not match. */
    export function parse(raw: Json): RestoreRequest {
      return WireDecode.parse("RestoreRequest", RestoreRequest.MODEL, raw);
    }
    /** A `RestoreRequest` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: RestoreRequest): RestoreRequest {
      return { ...fields };
    }
  }
  export namespace RowReorderBodySchema {
    /** The wire JSON `raw` as a new `RowReorderBodySchema`; throws a typed error when it does not match. */
    export function parse(raw: Json): RowReorderBodySchema {
      return WireDecode.parse("RowReorderBodySchema", RowReorderBodySchema.MODEL, raw);
    }
    /** A `RowReorderBodySchema` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: RowReorderBodySchema): RowReorderBodySchema {
      return { ...fields };
    }
  }
  export namespace Sheet {
    /** The wire JSON `raw` as a new `Sheet`; throws a typed error when it does not match. */
    export function parse(raw: Json): Sheet {
      return WireDecode.parse("Sheet", Sheet.MODEL, raw);
    }
    /** A `Sheet` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: Sheet): Sheet {
      return { ...fields };
    }
  }
  export namespace UserSheet {
    /** The wire JSON `raw` as a new `UserSheet`; throws a typed error when it does not match. */
    export function parse(raw: Json): UserSheet {
      return WireDecode.parse("UserSheet", UserSheet.MODEL, raw);
    }
    /** A `UserSheet` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: UserSheet): UserSheet {
      return { ...fields };
    }
  }
  export namespace UserSheetWithRows {
    /** The wire JSON `raw` as a new `UserSheetWithRows`; throws a typed error when it does not match. */
    export function parse(raw: Json): UserSheetWithRows {
      return WireDecode.parse("UserSheetWithRows", UserSheetWithRows.MODEL, raw);
    }
    /** A `UserSheetWithRows` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: UserSheetWithRows): UserSheetWithRows {
      return { ...fields };
    }
  }
  export namespace RowReorderResponse {
    /** The wire JSON `raw` as a new `RowReorderResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): RowReorderResponse {
      return WireDecode.parse("RowReorderResponse", RowReorderResponse.MODEL, raw);
    }
    /** A `RowReorderResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: RowReorderResponse): RowReorderResponse {
      return { ...fields };
    }
  }
  export namespace SheetReorderBodySchema {
    /** The wire JSON `raw` as a new `SheetReorderBodySchema`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetReorderBodySchema {
      return WireDecode.parse("SheetReorderBodySchema", SheetReorderBodySchema.MODEL, raw);
    }
    /** A `SheetReorderBodySchema` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetReorderBodySchema): SheetReorderBodySchema {
      return { ...fields };
    }
  }
  export namespace SheetReorderResponse {
    /** The wire JSON `raw` as a new `SheetReorderResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetReorderResponse {
      return WireDecode.parse("SheetReorderResponse", SheetReorderResponse.MODEL, raw);
    }
    /** A `SheetReorderResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetReorderResponse): SheetReorderResponse {
      return { ...fields };
    }
  }
  export namespace SheetRowDataResponse {
    /** The wire JSON `raw` as a new `SheetRowDataResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetRowDataResponse {
      return WireDecode.parse("SheetRowDataResponse", SheetRowDataResponse.MODEL, raw);
    }
    /** A `SheetRowDataResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SheetRowDataResponse): SheetRowDataResponse {
      return { ...fields };
    }
  }
  export namespace SheetUpdateAttributes {
    /** The wire JSON `raw` as a new `SheetUpdateAttributes`; throws a typed error when it does not match. */
    export function parse(raw: Json): SheetUpdateAttributes {
      return WireDecode.parse("SheetUpdateAttributes", SheetUpdateAttributes.MODEL, raw);
    }
  }
  export namespace SlotCell {
    /** The wire JSON `raw` as a new `SlotCell`; throws a typed error when it does not match. */
    export function parse(raw: Json): SlotCell {
      return WireDecode.parse("SlotCell", SlotCell.MODEL, raw);
    }
    /** A `SlotCell` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SlotCell): SlotCell {
      return { ...fields };
    }
  }
  export namespace SyncRange {
    /** The wire JSON `raw` as a new `SyncRange`; throws a typed error when it does not match. */
    export function parse(raw: Json): SyncRange {
      return WireDecode.parse("SyncRange", SyncRange.MODEL, raw);
    }
    /** A `SyncRange` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SyncRange): SyncRange {
      return { ...fields };
    }
  }
  export namespace SyncRowRequest {
    /** The wire JSON `raw` as a new `SyncRowRequest`; throws a typed error when it does not match. */
    export function parse(raw: Json): SyncRowRequest {
      return WireDecode.parse("SyncRowRequest", SyncRowRequest.MODEL, raw);
    }
    /** A `SyncRowRequest` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SyncRowRequest): SyncRowRequest {
      return { ...fields };
    }
  }
  export namespace SyncRowResponse {
    /** The wire JSON `raw` as a new `SyncRowResponse`; throws a typed error when it does not match. */
    export function parse(raw: Json): SyncRowResponse {
      return WireDecode.parse("SyncRowResponse", SyncRowResponse.MODEL, raw);
    }
    /** A `SyncRowResponse` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: SyncRowResponse): SyncRowResponse {
      return { ...fields };
    }
  }
  export namespace TaskAttributes {
    /** The wire JSON `raw` as a new `TaskAttributes`; throws a typed error when it does not match. */
    export function parse(raw: Json): TaskAttributes {
      return WireDecode.parse("TaskAttributes", TaskAttributes.MODEL, raw);
    }
    /** A `TaskAttributes` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: TaskAttributes): TaskAttributes {
      return { ...fields };
    }
  }
  export namespace Task {
    /** The wire JSON `raw` as a new `Task`; throws a typed error when it does not match. */
    export function parse(raw: Json): Task {
      return WireDecode.parse("Task", Task.MODEL, raw);
    }
    /** A `Task` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: Task): Task {
      return { ...fields };
    }
  }
  export namespace TaskCell {
    /** The wire JSON `raw` as a new `TaskCell`; throws a typed error when it does not match. */
    export function parse(raw: Json): TaskCell {
      return WireDecode.parse("TaskCell", TaskCell.MODEL, raw);
    }
    /** A `TaskCell` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: TaskCell): TaskCell {
      return { ...fields };
    }
  }
  export namespace TaskUpdateAttributes {
    /** The wire JSON `raw` as a new `TaskUpdateAttributes`; throws a typed error when it does not match. */
    export function parse(raw: Json): TaskUpdateAttributes {
      return WireDecode.parse("TaskUpdateAttributes", TaskUpdateAttributes.MODEL, raw);
    }
  }
  export namespace TemplateRowDef {
    /** The wire JSON `raw` as a new `TemplateRowDef`; throws a typed error when it does not match. */
    export function parse(raw: Json): TemplateRowDef {
      return WireDecode.parse("TemplateRowDef", TemplateRowDef.MODEL, raw);
    }
    /** A `TemplateRowDef` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: TemplateRowDef): TemplateRowDef {
      return { ...fields };
    }
  }
  export namespace TimezoneCell {
    /** The wire JSON `raw` as a new `TimezoneCell`; throws a typed error when it does not match. */
    export function parse(raw: Json): TimezoneCell {
      return WireDecode.parse("TimezoneCell", TimezoneCell.MODEL, raw);
    }
    /** A `TimezoneCell` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: TimezoneCell): TimezoneCell {
      return { ...fields };
    }
  }
  export namespace UserSheetAccessSelect {
    /** The wire JSON `raw` as a new `UserSheetAccessSelect`; throws a typed error when it does not match. */
    export function parse(raw: Json): UserSheetAccessSelect {
      return WireDecode.parse("UserSheetAccessSelect", UserSheetAccessSelect.MODEL, raw);
    }
    /** A `UserSheetAccessSelect` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: UserSheetAccessSelect): UserSheetAccessSelect {
      return { ...fields };
    }
  }
  export namespace UserSheetAttributes {
    /** The wire JSON `raw` as a new `UserSheetAttributes`; throws a typed error when it does not match. */
    export function parse(raw: Json): UserSheetAttributes {
      return WireDecode.parse("UserSheetAttributes", UserSheetAttributes.MODEL, raw);
    }
  }
  export namespace WeatherCell {
    /** The wire JSON `raw` as a new `WeatherCell`; throws a typed error when it does not match. */
    export function parse(raw: Json): WeatherCell {
      return WireDecode.parse("WeatherCell", WeatherCell.MODEL, raw);
    }
    /** A `WeatherCell` from every one of its fields (a nullable one stated as `null`). */
    export function make(fields: WeatherCell): WeatherCell {
      return { ...fields };
    }
  }
  export type Routes = {
    "/api/calendars": {
      get: {
        response: CalendarList[]
      }
      post: {
        config: {
          body: AddCalendarRequest
        }
      }
    }
    "/api/rows": {
      post: {
        config: {
          body: NewRow
        }
        response: CreatedRow
      }
    }
    "/api/rows/all": {
      get: {
        config: {
          query: {
            deleted: Option<boolean>,
            page: Option<number>,
            pageSize: Option<number>,
            sortBy: Option<string>,
            sortDirection: Option<string>,
            rowType: Option<string>,
          }
        }
        response: PaginatedRowsResponse
      }
    }
    "/api/rows/linkable": {
      get: {
        response: LinkableRow[]
      }
      post: {
        config: {
          body: LinkRowRequest
        }
      }
    }
    "/api/rows/{rowKey}": {
      get: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
        }
        response: Option<SheetRowDb>
      }
      delete: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
        }
      }
      patch: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
          body: PatchRow_PendingValidation
        }
        response: PatchRowResponse
      }
    }
    "/api/rows/{rowKey}/cells": {
      get: {
        config: {
          query: {
            start: NaiveDate,
            end: NaiveDate,
            mock_sync_delay: Option<number>,
            booking_id: Option<Uuid>,
          }
          path: {
            rowKey: RowNetworkKey
          }
        }
        response: SheetCell[]
      }
      post: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
          body: CellWriteRequest
        }
        response: CellWriteResponse
      }
    }
    "/api/rows/{rowKey}/cells/generate": {
      post: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
          body: Body
        }
        response: SheetCell
      }
    }
    "/api/rows/{rowKey}/exists": {
      get: {
        config: {
          query: {
            excludeRowId: Option<RowNetworkKey>,
          }
          path: {
            rowKey: RowNetworkKey
          }
        }
        response: LookupKeyExistsResponse
      }
    }
    "/api/rows/{rowKey}/restore": {
      post: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
          body: RestoreRequest
        }
      }
    }
    "/api/rows/{rowKey}/sync": {
      post: {
        config: {
          path: {
            rowKey: RowNetworkKey
          }
          body: SyncRowRequest
        }
        response: SyncRowResponse
      }
    }
    "/api/sheets": {
      get: {
        response: Option<UserSheetWithRows>
      }
      post: {
        config: {
          body: NewUserSheet
        }
        response: UserSheetWithRows
      }
    }
    "/api/sheets/list": {
      get: {
        config: {
          query: {
            deleted: Option<boolean>,
          }
        }
      }
    }
    "/api/sheets/{sheetKey}": {
      get: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
        response: Option<UserSheetWithRows>
      }
      delete: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
      }
      patch: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
          body: UserSheetAttributes
        }
      }
    }
    "/api/sheets/{sheetKey}/acl": {
      get: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
        response: UserSheetAccessSelect[]
      }
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
          body: CreateAcl
        }
      }
      delete: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
          body: DeleteAcl
        }
      }
    }
    "/api/sheets/{sheetKey}/cells": {
      get: {
        config: {
          query: {
            start: NaiveDate,
            end: NaiveDate,
            mock_sync_delay: Option<number>,
            booking_id: Option<Uuid>,
          }
          path: {
            sheetKey: SheetNetworkKey
          }
        }
        response: Option<SheetRowDataResponse[]>
      }
    }
    "/api/sheets/{sheetKey}/cells/batch": {
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
          body: BatchCellsRequest
        }
        response: Option<BatchCellsResponse>
      }
    }
    "/api/sheets/{sheetKey}/reorder": {
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
          body: SheetReorderBodySchema
        }
        response: SheetReorderResponse
      }
    }
    "/api/sheets/{sheetKey}/resolve": {
      get: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
        response: Option<ResolvedSheetKey>
      }
    }
    "/api/sheets/{sheetKey}/resolve/rows": {
      get: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
        response: Option<ResolvedRowsResponse>
      }
    }
    "/api/sheets/{sheetKey}/restore": {
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
      }
    }
    "/api/sheets/{sheetKey}/row": {
      get: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
        }
        response: Option<SheetRow[]>
      }
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
          }
          body: NewRowBody
        }
        response: CreateRowResponse
      }
    }
    "/api/sheets/{sheetKey}/row/{rowKey}": {
      delete: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
            rowKey: RowNetworkKey
          }
        }
      }
    }
    "/api/sheets/{sheetKey}/row/{rowKey}/hydrate-children": {
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
            rowKey: RowNetworkKey
          }
          body: HydrateChildrenBody
        }
        response: HydrateChildrenResponse
      }
    }
    "/api/sheets/{sheetKey}/row/{rowKey}/reorder": {
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
            rowKey: RowNetworkKey
          }
          body: RowReorderBodySchema
        }
        response: RowReorderResponse
      }
    }
    "/api/sheets/{sheetKey}/row/{rowKey}/sync-reset": {
      post: {
        config: {
          path: {
            sheetKey: SheetNetworkKey
            rowKey: RowNetworkKey
          }
        }
      }
    }
    "/api/tasks": {
      get: {
        response: Task[]
      }
      post: {
        config: {
          body: TaskAttributes
        }
        response: Task
      }
    }
    "/api/tasks/list/test": {
      get: {
        response: Task[]
      }
    }
    "/api/tasks/{taskId}": {
      get: {
        config: {
          path: {
            taskId: TaskId
          }
        }
        response: Task
      }
      delete: {
        config: {
          path: {
            taskId: TaskId
          }
        }
      }
      patch: {
        config: {
          path: {
            taskId: TaskId
          }
          body: TaskUpdateAttributes
        }
      }
    }
  };
  export const REVIVERS = {
    "/api/calendars": {
      parse: {
        get: (p: Json[]) => p.map(CalendarList.parse),
      },
    },
    "/api/rows": {
      parse: {
        post: CreatedRow.parse,
      },
    },
    "/api/rows/all": {
      parse: {
        get: PaginatedRowsResponse.parse,
      },
    },
    "/api/rows/linkable": {
      parse: {
        get: (p: Json[]) => p.map(LinkableRow.parse),
      },
    },
    "/api/rows/{rowKey}": {
      parse: {
        get: SheetRowDb.parse,
        patch: PatchRowResponse.parse,
      },
    },
    "/api/rows/{rowKey}/cells": {
      parse: {
        get: (p: Json[]) => p.map(SheetCellSchema.parse),
        post: CellWriteResponse.parse,
      },
    },
    "/api/rows/{rowKey}/cells/generate": {
      parse: {
        post: SheetCellSchema.parse,
      },
    },
    "/api/rows/{rowKey}/exists": {
      parse: {
        get: LookupKeyExistsResponse.parse,
      },
    },
    "/api/rows/{rowKey}/sync": {
      parse: {
        post: SyncRowResponse.parse,
      },
    },
    "/api/sheets": {
      parse: {
        get: UserSheetWithRows.parse,
        post: UserSheetWithRows.parse,
      },
    },
    "/api/sheets/{sheetKey}": {
      parse: {
        get: UserSheetWithRows.parse,
      },
    },
    "/api/sheets/{sheetKey}/acl": {
      parse: {
        get: (p: Json[]) => p.map(UserSheetAccessSelect.parse),
      },
    },
    "/api/sheets/{sheetKey}/cells": {
      parse: {
        get: (p: Json[]) => p.map(SheetRowDataResponse.parse),
      },
    },
    "/api/sheets/{sheetKey}/cells/batch": {
      parse: {
        post: BatchCellsResponse.parse,
      },
    },
    "/api/sheets/{sheetKey}/reorder": {
      parse: {
        post: SheetReorderResponse.parse,
      },
    },
    "/api/sheets/{sheetKey}/resolve": {
      parse: {
        get: ResolvedSheetKey.parse,
      },
    },
    "/api/sheets/{sheetKey}/resolve/rows": {
      parse: {
        get: ResolvedRowsResponse.parse,
      },
    },
    "/api/sheets/{sheetKey}/row": {
      parse: {
        get: (p: Json[]) => p.map(SheetRow.parse),
        post: CreateRowResponse.parse,
      },
    },
    "/api/sheets/{sheetKey}/row/{rowKey}/hydrate-children": {
      parse: {
        post: HydrateChildrenResponse.parse,
      },
    },
    "/api/sheets/{sheetKey}/row/{rowKey}/reorder": {
      parse: {
        post: RowReorderResponse.parse,
      },
    },
    "/api/tasks": {
      parse: {
        get: (p: Json[]) => p.map(Task.parse),
        post: Task.parse,
      },
    },
    "/api/tasks/list/test": {
      parse: {
        get: (p: Json[]) => p.map(Task.parse),
      },
    },
    "/api/tasks/{taskId}": {
      parse: {
        get: Task.parse,
      },
    },
  };
}
